import React, { useState, useEffect, useRef } from 'react';
import { 
  db, 
  auth,
  handleFirestoreError,
  OperationType
} from '../firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  doc, 
  getDocs, 
  setDoc,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { 
  MessageCircle, 
  X, 
  Send, 
  Paperclip, 
  Check, 
  CheckCheck, 
  Users, 
  Plus, 
  Image as ImageIcon, 
  FileText, 
  Video as VideoIcon, 
  Music, 
  Loader2, 
  ArrowLeft,
  Search,
  Check as CheckIcon,
  Circle
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { UserProfile, ChatMessage, Conversation, ChatAttachment } from '../types';

export const ChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'conversations' | 'new-chat' | 'create-group'>('conversations');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [currentUserProfile, setCurrentUserProfile] = useState<UserProfile | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Message Input
  const [messageText, setMessageText] = useState('');
  const [attachment, setAttachment] = useState<ChatAttachment | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Group creation state
  const [groupName, setGroupName] = useState('');
  const [selectedGroupMembers, setSelectedGroupMembers] = useState<string[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const currentUser = auth.currentUser;

  // 1. Fetch Current User Profile
  useEffect(() => {
    if (!currentUser) return;
    const unsubscribe = onSnapshot(doc(db, 'users', currentUser.uid), (snapshot) => {
      if (snapshot.exists()) {
        setCurrentUserProfile(snapshot.data() as UserProfile);
      }
    });
    return () => unsubscribe();
  }, [currentUser]);

  // 2. Fetch all registered users
  useEffect(() => {
    if (!currentUser || !isOpen) return;
    const q = query(collection(db, 'users'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allUsers = snapshot.docs.map(doc => doc.data() as UserProfile);
      setUsers(allUsers.filter(u => u.uid !== currentUser.uid && u.isActive !== false));
    });
    return () => unsubscribe();
  }, [currentUser, isOpen]);

  // 3. Fetch user's conversations
  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'conversations'),
      where('members', 'array-contains', currentUser.uid)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const convList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Conversation));
      // Sort conversations by last message timestamp or creation
      convList.sort((a, b) => {
        const timeA = a.lastMessage?.timestamp || a.createdAt;
        const timeB = b.lastMessage?.timestamp || b.createdAt;
        return new Date(timeB).getTime() - new Date(timeA).getTime();
      });
      setConversations(convList);

      // Keep active conversation state updated in real-time
      if (activeConversation) {
        const updated = convList.find(c => c.id === activeConversation.id);
        if (updated) setActiveConversation(updated);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'conversations');
    });

    return () => unsubscribe();
  }, [currentUser, activeConversation?.id]);

  // 4. Fetch messages for the active conversation
  useEffect(() => {
    if (!currentUser || !activeConversation) {
      setMessages([]);
      return;
    }

    const messagesRef = collection(db, 'conversations', activeConversation.id, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ChatMessage));
      setMessages(msgList);

      // Auto scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);

      // Real-time receipts logic:
      // If we are looking at this conversation, mark all incoming unread messages as read
      const unreadMessages = msgList.filter(
        msg => msg.senderId !== currentUser.uid && msg.status !== 'read'
      );

      unreadMessages.forEach(async (msg) => {
        const msgDocRef = doc(db, 'conversations', activeConversation.id, 'messages', msg.id);
        await updateDoc(msgDocRef, { 
          status: 'read',
          [`readBy.${currentUser.uid}`]: true
        }).catch(err => console.warn("Error marking read:", err));
      });

      // Update the main conversation document's last message status if needed
      if (unreadMessages.length > 0 && activeConversation.lastMessage?.senderId !== currentUser.uid) {
        const convRef = doc(db, 'conversations', activeConversation.id);
        updateDoc(convRef, {
          'lastMessage.status': 'read'
        }).catch(err => console.warn("Error marking conversation last message read:", err));
      }
    }, (error) => {
      console.error("Error loading messages:", error);
    });

    return () => unsubscribe();
  }, [currentUser, activeConversation?.id]);

  // 5. Delivery receipts tracking:
  // When conversations load, if we have unreceived messages (status: 'sent') where we are a recipient,
  // mark them as 'delivered'.
  useEffect(() => {
    if (!currentUser || conversations.length === 0) return;

    conversations.forEach(async (conv) => {
      // Find sent messages inside these conversations where the current user is a recipient and status is 'sent'
      const messagesRef = collection(db, 'conversations', conv.id, 'messages');
      const q = query(messagesRef, where('status', '==', 'sent'));
      const snapshot = await getDocs(q).catch(() => null);
      if (!snapshot) return;

      snapshot.docs.forEach(async (messageDoc) => {
        const msg = messageDoc.data() as ChatMessage;
        if (msg.senderId !== currentUser.uid) {
          await updateDoc(doc(db, 'conversations', conv.id, 'messages', messageDoc.id), {
            status: 'delivered',
            [`deliveredTo.${currentUser.uid}`]: true
          }).catch(err => console.warn("Error marking delivered:", err));
        }
      });
    });
  }, [currentUser, conversations.length]);

  // Typing Indicator Trigger
  const handleTyping = () => {
    if (!currentUser || !activeConversation) return;

    const convRef = doc(db, 'conversations', activeConversation.id);
    
    // Set typing to true
    updateDoc(convRef, {
      [`typing.${currentUser.uid}`]: true
    }).catch(() => null);

    // Clear previous timeout
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    // Set timeout to reset typing
    typingTimeoutRef.current = setTimeout(() => {
      updateDoc(convRef, {
        [`typing.${currentUser.uid}`]: false
      }).catch(() => null);
    }, 2500);
  };

  // Select / Create a direct chat with a user
  const handleStartDirectChat = async (targetUser: UserProfile) => {
    if (!currentUser) return;

    // Check if direct conversation already exists between these 2 users
    const sortedMembers = [currentUser.uid, targetUser.uid].sort();
    const existingId = sortedMembers.join('_');

    const convRef = doc(db, 'conversations', existingId);
    const convSnap = await getDoc(convRef).catch(() => null);

    let conversationData: Conversation;

    if (convSnap && convSnap.exists()) {
      conversationData = { id: convSnap.id, ...convSnap.data() } as Conversation;
    } else {
      // Create a brand new direct conversation
      const newConv: Omit<Conversation, 'id'> = {
        isGroup: false,
        members: sortedMembers,
        createdAt: new Date().toISOString(),
        typing: {
          [currentUser.uid]: false,
          [targetUser.uid]: false
        }
      };
      await setDoc(convRef, newConv).catch(err => {
        toast.error("Failed to start chat session");
        throw err;
      });
      conversationData = { id: existingId, ...newConv };
    }

    setActiveConversation(conversationData);
    setActiveTab('conversations');
  };

  // Create group chat
  const handleCreateGroup = async () => {
    if (!currentUser || !currentUserProfile) return;
    if (!groupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    if (selectedGroupMembers.length === 0) {
      toast.error("Please select at least one member");
      return;
    }

    const isGroupCreatorAuthorized = [
      'admin', 
      'manager', 
      'md', 
      'ops_manager'
    ].includes(currentUserProfile.role);

    if (!isGroupCreatorAuthorized) {
      toast.error("Unauthorized: Only Admin, Managers, MD, or Senior Operations can create groups.");
      return;
    }

    const allMembers = [currentUser.uid, ...selectedGroupMembers];
    const groupTypingObj: { [uid: string]: boolean } = {};
    allMembers.forEach(uid => { groupTypingObj[uid] = false; });

    const newGroup: Omit<Conversation, 'id'> = {
      isGroup: true,
      name: groupName,
      creatorId: currentUser.uid,
      members: allMembers,
      createdAt: new Date().toISOString(),
      typing: groupTypingObj
    };

    try {
      const docRef = await addDoc(collection(db, 'conversations'), newGroup);
      toast.success(`Group "${groupName}" created successfully!`);
      
      // Reset group states
      setGroupName('');
      setSelectedGroupMembers([]);
      setActiveConversation({ id: docRef.id, ...newGroup });
      setActiveTab('conversations');
    } catch (err) {
      console.error(err);
      toast.error("Failed to create group");
    }
  };

  // Send Attachment Handler
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 600 * 1024) {
      toast.error("File is too large! Maximum allowed size is 600KB.");
      return;
    }

    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      const base64Url = reader.result as string;
      
      // Map file extension/type to chat attachment category
      let type: ChatAttachment['type'] = 'document';
      if (file.type.startsWith('image/')) type = 'image';
      else if (file.type.startsWith('video/')) type = 'video';
      else if (file.type.startsWith('audio/')) type = 'audio';
      else if (file.type === 'application/pdf') type = 'pdf';

      setAttachment({
        url: base64Url,
        name: file.name,
        type: type,
        size: file.size
      });
      setIsUploading(false);
      toast.success("Attachment ready to send!");
    };
    reader.onerror = () => {
      toast.error("Failed to read file.");
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  };

  // Send Message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !activeConversation) return;
    if (!messageText.trim() && !attachment) return;

    // Determine initial status of direct message based on target presence
    let initialStatus: 'sent' | 'delivered' = 'sent';
    if (!activeConversation.isGroup) {
      const otherMemberId = activeConversation.members.find(m => m !== currentUser.uid);
      if (otherMemberId) {
        const otherUser = users.find(u => u.uid === otherMemberId);
        if (otherUser?.isOnline) {
          initialStatus = 'delivered';
        }
      }
    }

    const newMessagePayload: Omit<ChatMessage, 'id'> = {
      senderId: currentUser.uid,
      senderName: currentUserProfile?.displayName || currentUser.email?.split('@')[0] || 'User',
      text: messageText,
      timestamp: new Date().toISOString(),
      status: initialStatus
    };

    if (attachment) {
      newMessagePayload.attachment = attachment;
    }

    // Reset inputs immediately for responsive feel
    setMessageText('');
    setAttachment(null);

    try {
      const messagesRef = collection(db, 'conversations', activeConversation.id, 'messages');
      await addDoc(messagesRef, newMessagePayload);

      // Update typing indicator to false immediately on send
      const convRef = doc(db, 'conversations', activeConversation.id);
      updateDoc(convRef, {
        [`typing.${currentUser.uid}`]: false,
        lastMessage: {
          text: attachment ? `📎 ${attachment.name}` : newMessagePayload.text,
          senderId: currentUser.uid,
          senderName: newMessagePayload.senderName,
          timestamp: newMessagePayload.timestamp,
          status: initialStatus
        }
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to send message");
    }
  };

  // Helper: Get user's online indicator
  const getOtherParticipant = (conv: Conversation) => {
    if (!currentUser) return null;
    const targetId = conv.members.find(m => m !== currentUser.uid);
    return users.find(u => u.uid === targetId) || null;
  };

  // Check if someone else is typing in current conversation
  const getTypingUsers = () => {
    if (!currentUser || !activeConversation || !activeConversation.typing) return null;
    const typingList: string[] = [];
    Object.keys(activeConversation.typing).forEach(uid => {
      if (uid !== currentUser.uid && activeConversation.typing?.[uid]) {
        // Find display name
        const userProfile = users.find(u => u.uid === uid);
        typingList.push(userProfile?.displayName || 'Someone');
      }
    });
    if (typingList.length === 0) return null;
    if (typingList.length === 1) return `${typingList[0]} is typing...`;
    return 'Multiple users typing...';
  };

  const isEligibleToCreateGroup = currentUserProfile && [
    'admin', 
    'manager', 
    'md', 
    'ops_manager'
  ].includes(currentUserProfile.role);

  // Count unread messages across all chats
  const totalUnreadCount = conversations.reduce((acc, conv) => {
    if (conv.lastMessage && conv.lastMessage.senderId !== currentUser?.uid && conv.lastMessage.status !== 'read') {
      return acc + 1;
    }
    return acc;
  }, 0);

  return (
    <>
      {/* Floating Action Bubble */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          id="chat-bubble-btn"
          onClick={() => setIsOpen(!isOpen)}
          className="relative w-16 h-16 bg-blue-900 hover:bg-orange-600 text-white rounded-full flex items-center justify-center shadow-2xl active:scale-95 transition-all cursor-pointer group hover:rotate-6"
        >
          {isOpen ? (
            <X className="w-7 h-7" />
          ) : (
            <MessageCircle className="w-8 h-8 group-hover:scale-110 transition-transform" />
          )}

          {totalUnreadCount > 0 && !isOpen && (
            <span className="absolute -top-1.5 -right-1.5 bg-orange-500 text-white text-[11px] font-black w-6 h-6 rounded-full flex items-center justify-center animate-bounce border-2 border-slate-50">
              {totalUnreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Main Chat Dialog Box */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed bottom-24 right-6 z-50 w-[420px] max-w-[calc(100vw-3rem)] h-[620px] max-h-[calc(100vh-8rem)] bg-white rounded-3xl shadow-[0_25px_60px_-15px_rgba(30,58,138,0.35)] border border-slate-100 overflow-hidden flex flex-col">
            
            {/* Top Header */}
            <div className="bg-blue-900 px-6 py-5 text-white flex items-center justify-between shadow-md">
              {activeConversation ? (
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => setActiveConversation(null)}
                    className="p-1.5 hover:bg-white/10 rounded-xl transition-colors text-blue-200 hover:text-white"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div>
                    <h3 className="font-extrabold tracking-tight text-base truncate max-w-[200px]">
                      {activeConversation.isGroup 
                        ? activeConversation.name 
                        : getOtherParticipant(activeConversation)?.displayName || 'Direct Chat'
                      }
                    </h3>
                    <p className="text-[10px] font-bold tracking-wider uppercase text-blue-200">
                      {activeConversation.isGroup 
                        ? `${activeConversation.members.length} members`
                        : getOtherParticipant(activeConversation)?.isOnline ? 'Online' : 'Offline'
                      }
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <h3 className="font-extrabold tracking-tight text-lg">Bright Chat</h3>
                  <p className="text-xs text-blue-200 font-medium">Internal Workspace Communications</p>
                </div>
              )}
              
              <button 
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/10 rounded-xl transition-colors text-blue-200 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Chat Panels */}
            <div className="flex-1 overflow-hidden flex flex-col bg-slate-50/50">
              
              {/* IF CHAT ROOM IS OPENED */}
              {activeConversation ? (
                <>
                  {/* Messages Scroll Area */}
                  <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {messages.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-2">
                        <MessageCircle className="w-10 h-10 text-slate-300 stroke-[1.5]" />
                        <p className="text-xs font-semibold italic">No messages yet. Send a greeting!</p>
                      </div>
                    ) : (
                      messages.map((msg) => {
                        const isMe = msg.senderId === currentUser?.uid;
                        return (
                          <div 
                            key={msg.id} 
                            className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}
                          >
                            <span className="text-[10px] font-bold text-slate-400 mb-1 px-1">
                              {isMe ? 'You' : msg.senderName}
                            </span>
                            <div 
                              className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm shadow-sm ${
                                isMe 
                                  ? 'bg-blue-900 text-white rounded-tr-none' 
                                  : 'bg-white text-slate-800 rounded-tl-none border border-slate-100'
                              }`}
                            >
                              {/* Render Attachments if present */}
                              {msg.attachment && (
                                <div className="mb-2 max-w-full rounded-xl overflow-hidden border border-slate-100/10">
                                  {msg.attachment.type === 'image' && (
                                    <img 
                                      src={msg.attachment.url} 
                                      alt={msg.attachment.name} 
                                      className="max-h-48 object-cover rounded-xl"
                                      referrerPolicy="no-referrer"
                                    />
                                  )}
                                  {msg.attachment.type === 'video' && (
                                    <video 
                                      src={msg.attachment.url} 
                                      controls 
                                      className="max-h-48 rounded-xl bg-black" 
                                    />
                                  )}
                                  {msg.attachment.type === 'audio' && (
                                    <audio 
                                      src={msg.attachment.url} 
                                      controls 
                                      className="w-full h-10 mt-1 scale-95" 
                                    />
                                  )}
                                  {msg.attachment.type === 'pdf' && (
                                    <a 
                                      href={msg.attachment.url} 
                                      download={msg.attachment.name}
                                      className="flex items-center gap-2 p-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl transition-colors font-semibold text-xs"
                                    >
                                      <FileText className="w-5 h-5 text-red-500 shrink-0" />
                                      <span className="truncate max-w-[150px]">{msg.attachment.name}</span>
                                    </a>
                                  )}
                                  {msg.attachment.type === 'document' && (
                                    <a 
                                      href={msg.attachment.url} 
                                      download={msg.attachment.name}
                                      className="flex items-center gap-2 p-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl transition-colors font-semibold text-xs"
                                    >
                                      <FileText className="w-5 h-5 text-blue-500 shrink-0" />
                                      <span className="truncate max-w-[150px]">{msg.attachment.name}</span>
                                    </a>
                                  )}
                                </div>
                              )}

                              {/* Render Text */}
                              {msg.text && <p className="leading-relaxed break-words">{msg.text}</p>}

                              {/* Footer ticks and time */}
                              <div className="flex items-center justify-end gap-1.5 mt-1 text-[9px] opacity-70">
                                <span>
                                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                {isMe && (
                                  <span>
                                    {msg.status === 'read' ? (
                                      <CheckCheck className="w-3.5 h-3.5 text-sky-400" />
                                    ) : msg.status === 'delivered' ? (
                                      <CheckCheck className="w-3.5 h-3.5 text-slate-300" />
                                    ) : (
                                      <Check className="w-3.5 h-3.5 text-slate-300" />
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Typing indicator state */}
                  {getTypingUsers() && (
                    <div className="px-5 py-1.5 text-xs text-slate-400 italic font-medium animate-pulse">
                      {getTypingUsers()}
                    </div>
                  )}

                  {/* Attachment Preview Box */}
                  {attachment && (
                    <div className="px-5 py-3 bg-blue-50/50 border-t border-blue-100/50 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-xs font-bold text-blue-900 truncate">
                        <Paperclip className="w-4 h-4 text-blue-700 shrink-0" />
                        <span className="truncate max-w-[250px]">{attachment.name}</span>
                        <span className="text-[10px] text-slate-400">({(attachment.size || 0) > 1024 ? `${Math.round((attachment.size || 0)/1024)} KB` : `${attachment.size} B`})</span>
                      </div>
                      <button 
                        onClick={() => setAttachment(null)}
                        className="p-1 hover:bg-blue-100 rounded-full text-blue-900 transition-colors cursor-pointer"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Chat Input form */}
                  <form 
                    onSubmit={handleSendMessage} 
                    className="p-4 bg-white border-t border-slate-100 flex items-center gap-2.5"
                  >
                    <input 
                      type="file" 
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="hidden"
                      accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                    />
                    
                    <button
                      type="button"
                      disabled={isUploading}
                      onClick={() => fileInputRef.current?.click()}
                      className="p-3 bg-slate-50 hover:bg-slate-100 text-slate-500 hover:text-slate-800 rounded-2xl transition-colors cursor-pointer flex items-center justify-center shrink-0"
                      title="Attach file (Max 600KB)"
                    >
                      {isUploading ? (
                        <Loader2 className="w-5 h-5 animate-spin text-blue-900" />
                      ) : (
                        <Paperclip className="w-5 h-5" />
                      )}
                    </button>

                    <input
                      type="text"
                      placeholder="Type a message..."
                      value={messageText}
                      onChange={(e) => {
                        setMessageText(e.target.value);
                        handleTyping();
                      }}
                      className="flex-1 px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-800/10 font-medium"
                    />

                    <button
                      type="submit"
                      disabled={!messageText.trim() && !attachment}
                      className="p-3.5 bg-blue-900 hover:bg-orange-600 disabled:bg-slate-100 disabled:text-slate-300 text-white rounded-2xl transition-all shadow-md cursor-pointer flex items-center justify-center shrink-0"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </form>
                </>
              ) : (
                <>
                  {/* SEARCH & CONTROLS IN HOME CHAT PANEL */}
                  <div className="p-4 bg-white border-b border-slate-100 space-y-3 shrink-0">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setActiveTab('conversations')}
                        className={`flex-1 py-2 text-xs font-black rounded-xl transition-all ${
                          activeTab === 'conversations' 
                            ? 'bg-blue-900 text-white' 
                            : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                        }`}
                      >
                        Chats
                      </button>
                      <button
                        onClick={() => setActiveTab('new-chat')}
                        className={`flex-1 py-2 text-xs font-black rounded-xl transition-all ${
                          activeTab === 'new-chat' 
                            ? 'bg-blue-900 text-white' 
                            : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                        }`}
                      >
                        New Chat
                      </button>
                      {isEligibleToCreateGroup && (
                        <button
                          onClick={() => setActiveTab('create-group')}
                          className={`flex-1 py-2 text-xs font-black rounded-xl transition-all ${
                            activeTab === 'create-group' 
                              ? 'bg-blue-900 text-white' 
                              : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                          }`}
                        >
                          New Group
                        </button>
                      )}
                    </div>

                    <div className="relative">
                      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-bold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                      />
                    </div>
                  </div>

                  {/* ACTIVE TAB CONTENTS */}
                  <div className="flex-1 overflow-y-auto">
                    
                    {/* TAB 1: CONVERSATIONS LIST */}
                    {activeTab === 'conversations' && (
                      <div className="divide-y divide-slate-100/50">
                        {conversations.length === 0 ? (
                          <div className="h-48 flex flex-col items-center justify-center text-slate-400 gap-2">
                            <Users className="w-10 h-10 text-slate-300 stroke-[1.5]" />
                            <p className="text-xs font-semibold italic">No conversations. Start a new chat!</p>
                          </div>
                        ) : (
                          conversations
                            .filter(c => {
                              if (c.isGroup) {
                                return c.name?.toLowerCase().includes(searchQuery.toLowerCase());
                              } else {
                                const target = getOtherParticipant(c);
                                return target?.displayName?.toLowerCase().includes(searchQuery.toLowerCase());
                              }
                            })
                            .map((conv) => {
                              const targetUser = !conv.isGroup ? getOtherParticipant(conv) : null;
                              const isUnread = conv.lastMessage && conv.lastMessage.senderId !== currentUser?.uid && conv.lastMessage.status !== 'read';

                              return (
                                <button
                                  key={conv.id}
                                  onClick={() => setActiveConversation(conv)}
                                  className="w-full px-5 py-4 flex items-center justify-between gap-4 hover:bg-white transition-all text-left group cursor-pointer"
                                >
                                  <div className="flex items-center gap-3.5 min-w-0">
                                    <div className="relative shrink-0">
                                      {conv.isGroup ? (
                                        <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-900 shadow-sm">
                                          <Users className="w-6 h-6" />
                                        </div>
                                      ) : (
                                        <div className="w-12 h-12 bg-orange-100 text-orange-950 font-black rounded-2xl flex items-center justify-center shadow-sm">
                                          {targetUser?.displayName?.substring(0, 2).toUpperCase() || 'DM'}
                                        </div>
                                      )}
                                      
                                      {/* Online dot for direct chats */}
                                      {!conv.isGroup && targetUser?.isOnline && (
                                        <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 border-[3px] border-slate-50 rounded-full animate-pulse" />
                                      )}
                                    </div>

                                    <div className="min-w-0">
                                      <h4 className={`font-extrabold text-sm truncate text-slate-800 ${isUnread ? 'text-blue-900 font-black' : ''}`}>
                                        {conv.isGroup ? conv.name : targetUser?.displayName || 'Direct Chat'}
                                      </h4>
                                      <p className={`text-xs text-slate-400 truncate max-w-[180px] ${isUnread ? 'text-blue-800/90 font-bold' : ''}`}>
                                        {conv.typing && Object.values(conv.typing).some(v => v === true) ? (
                                          <span className="text-emerald-500 font-semibold animate-pulse">typing...</span>
                                        ) : conv.lastMessage ? (
                                          `${conv.lastMessage.senderName}: ${conv.lastMessage.text}`
                                        ) : (
                                          'No messages yet'
                                        )}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex flex-col items-end shrink-0 gap-1.5">
                                    {conv.lastMessage?.timestamp && (
                                      <span className="text-[10px] text-slate-400 font-bold">
                                        {new Date(conv.lastMessage.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    )}
                                    {isUnread && (
                                      <span className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                                    )}
                                  </div>
                                </button>
                              );
                            })
                        )}
                      </div>
                    )}

                    {/* TAB 2: START NEW DIRECT CHAT */}
                    {activeTab === 'new-chat' && (
                      <div className="divide-y divide-slate-100/50">
                        {users.filter(u => u.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) || u.email.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 ? (
                          <div className="h-48 flex flex-col items-center justify-center text-slate-400 gap-2">
                            <Users className="w-10 h-10 text-slate-300" />
                            <p className="text-xs font-semibold italic">No users found</p>
                          </div>
                        ) : (
                          users
                            .filter(u => u.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) || u.email.toLowerCase().includes(searchQuery.toLowerCase()))
                            .map((userObj) => (
                              <button
                                key={userObj.uid}
                                onClick={() => handleStartDirectChat(userObj)}
                                className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-white transition-all text-left cursor-pointer"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="relative">
                                    <div className="w-10 h-10 bg-orange-100 text-orange-950 rounded-xl flex items-center justify-center font-bold text-sm">
                                      {userObj.displayName?.substring(0, 2).toUpperCase() || 'U'}
                                    </div>
                                    {userObj.isOnline && (
                                      <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-emerald-500 border-2 border-slate-50 rounded-full animate-pulse" />
                                    )}
                                  </div>
                                  <div>
                                    <h4 className="font-bold text-sm text-slate-800">{userObj.displayName}</h4>
                                    <p className="text-xs text-slate-400 font-semibold">{userObj.role.toUpperCase()}</p>
                                  </div>
                                </div>
                                <Plus className="w-4 h-4 text-slate-400" />
                              </button>
                            ))
                        )}
                      </div>
                    )}

                    {/* TAB 3: CREATE GROUP */}
                    {activeTab === 'create-group' && (
                      <div className="p-5 space-y-4">
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Group Name</label>
                          <input
                            type="text"
                            placeholder="Enter group subject..."
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-100 border border-transparent rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-800/10 font-bold"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest block mb-2">Select Members ({selectedGroupMembers.length} selected)</label>
                          
                          <div className="divide-y divide-slate-100 border border-slate-100 rounded-2xl max-h-60 overflow-y-auto bg-white p-2 space-y-1">
                            {users.map((userObj) => {
                              const isSelected = selectedGroupMembers.includes(userObj.uid);
                              return (
                                <button
                                  key={userObj.uid}
                                  type="button"
                                  onClick={() => {
                                    if (isSelected) {
                                      setSelectedGroupMembers(prev => prev.filter(uid => uid !== userObj.uid));
                                    } else {
                                      setSelectedGroupMembers(prev => [...prev, userObj.uid]);
                                    }
                                  }}
                                  className={`w-full p-2.5 rounded-xl flex items-center justify-between text-left transition-colors cursor-pointer ${
                                    isSelected ? 'bg-blue-50/50' : 'hover:bg-slate-50'
                                  }`}
                                >
                                  <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 bg-orange-100 text-orange-950 rounded-lg flex items-center justify-center font-bold text-xs">
                                      {userObj.displayName?.substring(0, 2).toUpperCase()}
                                    </div>
                                    <div>
                                      <h5 className="text-xs font-extrabold text-slate-800">{userObj.displayName}</h5>
                                      <p className="text-[10px] font-bold text-slate-400">{userObj.role.toUpperCase()}</p>
                                    </div>
                                  </div>
                                  
                                  {isSelected ? (
                                    <CheckIcon className="w-4 h-4 text-blue-900 stroke-[3]" />
                                  ) : (
                                    <Plus className="w-4 h-4 text-slate-400" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <button
                          onClick={handleCreateGroup}
                          className="w-full py-3.5 bg-blue-900 hover:bg-orange-600 text-white rounded-2xl text-xs font-black uppercase tracking-wider transition-all shadow-md mt-2 cursor-pointer"
                        >
                          Create Group Chat
                        </button>
                      </div>
                    )}

                  </div>
                </>
              )}

            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
